import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { ClipStatePacket } from '@/shared/clipping/ClipStatePacket'
import {
  COMPILED_CLIP_HIGHLIGHT_COLOR,
  type CompiledClipState,
} from '@/shared/clipping/CompiledClipState'
import type { TSLModule } from '@/shared/clipping/TSLClipState'
import { createTslClipEvaluation } from '@/shared/clipping/TSLClipState'

type CadClipMaterialLike = LineMaterial & {
  isLine2NodeMaterial?: boolean
  setupDiffuseColor: (builder: unknown) => void
}

function getClipBase(material: LineMaterial): ((builder: unknown) => void) | undefined {
  return material.userData?.cadClipBaseSetupDiffuseColor as ((builder: unknown) => void) | undefined
}

/** 移除材质上的 CAD TSL 裁切装饰，恢复进入裁切前的 setup。 */
export function restoreCadClipMaterial(material: LineMaterial): void {
  const target = material as CadClipMaterialLike
  const base = getClipBase(material)
  if (!base) return
  target.setupDiffuseColor = base
  target.userData.cadClipBaseSetupDiffuseColor = undefined
  target.needsUpdate = true
}

/** CAD WebGPU 材质的共享裁切绑定。 */
export class CadClipBinding {
  readonly packet: ClipStatePacket

  constructor(
    private readonly _tsl: TSLModule,
    readonly state: CompiledClipState
  ) {
    this.packet = new ClipStatePacket(state)
  }

  applyToMaterial(material: LineMaterial): void {
    const target = material as CadClipMaterialLike
    if (!target.isLine2NodeMaterial) return
    const userData = (target.userData ??= {})
    const base = getClipBase(target) ?? target.setupDiffuseColor.bind(target)
    userData.cadClipBaseSetupDiffuseColor = base

    target.setupDiffuseColor = (builder) => {
      base(builder)
      const evaluation = createTslClipEvaluation(
        this._tsl,
        this.packet,
        this.state,
        this._tsl.positionWorld
      )
      evaluation.hidden.discard()
      this._tsl.If(evaluation.highlighted, () => {
        this._tsl.diffuseColor.assign(
          this._tsl.vec4(
            COMPILED_CLIP_HIGHLIGHT_COLOR.r,
            COMPILED_CLIP_HIGHLIGHT_COLOR.g,
            COMPILED_CLIP_HIGHLIGHT_COLOR.b,
            this._tsl.diffuseColor.a
          )
        )
      })
    }
    target.needsUpdate = true
  }

  restoreMaterial(material: LineMaterial): void {
    restoreCadClipMaterial(material)
  }

  dispose(): void {
    this.packet.dispose()
  }
}
